import React, { FC, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '$hooks';
import { IStoreWithStock } from '$models/product-types';
import { Flex } from '$components/layouts';
import { getStockType } from '$lib/helpers/stock.helper';
import { RouteKeys, useCMSRoute } from '~/store';
import { Text } from '$components/elements/text';
import { Link } from '$components/elements/link';
import { Button } from '$components/elements/button';
import { IStoreInformation, StockType } from '$models';

type Props = {
    store: IStoreWithStock;
    isSelected?: boolean;
    isLoading?: boolean;
    onStoreSelection?: (id?: string) => void;
    onPickup?: (store: IStoreInformation) => void;
    canPickup: boolean;
};

export const PickupStoreItem: FC<Props> = ({ store, onPickup, canPickup }): JSX.Element => {
    const { translate } = useTranslation();
    const [isLoadingTriggered, setIsLoadingTriggered] = useState(false);

    const stockText = useMemo(() => {
        if (store.hideStoreStockInformation) {
            return translate('productPage.clickCollect.noStock');
        }

        switch (getStockType(store.stock)) {
            case 'inStock':
                return translate('productPage.clickCollect.inStock');
            case 'lowStock':
                return translate('productPage.clickCollect.lowStock');
            case 'noStock':
            case 'unknown':
            default:
                return translate('productPage.clickCollect.noStock');
        }
    }, [store]);

    const pickupCallbackText = useMemo(() => {
        switch (getStockType(store.stock)) {
            case 'inStock':
                return translate('productPage.clickCollect.buttonText');
            case 'noStock':
            case 'unknown':
            default:
                return translate('productPage.clickCollect.buttonTextNoStock');
        }
    }, [store]);

    const { routes } = useCMSRoute();

    return (
        <ItemContainer gap="small">
            <Flex column grow>
                <Text fontWeight="Bold" size="14px" bottomMargin="1">
                    {store.customerName}
                </Text>
                <Flex gap="small" spacingBottom="extrasmall" wrap="wrap">
                    {routes[RouteKeys.StoreDetail] && (
                        <StoreLink
                            applyTextStyling={false}
                            href={`${routes[RouteKeys.StoreDetail]}/${store.customerWebName}`}
                        >
                            {translate('productPage.clickCollect.seeStoreInfo')}
                        </StoreLink>
                    )}
                    {/* <FavoriteStoreButton
                        onClick={() => onStoreSelection?.(store.customerId)}
                        disabled={isSelected}
                        variant="link"
                    >
                        {isSelected
                            ? translate('productPage.clickCollect.choosenFavorite')
                            : translate('productPage.clickCollect.chooseAsFavorite')}
                    </FavoriteStoreButton> */}
                </Flex>
                <StockText
                    variant="caption"
                    fontWeight="Bold"
                    type={store.hideStoreStockInformation ? 'unknown' : getStockType(store.stock)}
                >
                    {stockText}
                </StockText>
            </Flex>
            <CtaContainer>
                {canPickup && (
                    <PickupButton
                        onClick={() => {
                            if (isLoadingTriggered) return;
                            setIsLoadingTriggered(true);
                            onPickup?.(store);
                        }}
                        variant="inverted"
                        loading={isLoadingTriggered}
                    >
                        {pickupCallbackText}
                    </PickupButton>
                )}
            </CtaContainer>
        </ItemContainer>
    );
};

const ItemContainer = styled(Flex)(({ theme }) => ({
    backgroundColor: theme.colors.grey5,
    padding: `${theme.space[5]} ${theme.space[6]}`,
    borderRadius: '12px',
}));

const CtaContainer = styled.div({
    display: 'flex',
    flexShrink: 0,
    alignItems: 'flex-end',
});

const StoreLink = styled(Link)(({ theme }) => ({
    border: 'none',
    padding: '0',
    textDecoration: 'underline',
    fontSize: theme.fontSizes.xxs,
    fontWeight: theme.fontWeights.medium,
}));

// const FavoriteStoreButton = styled(Button)(
//     ({ theme }) => ({
//         fontSize: theme.fontSizes.xxs,
//         fontWeight: theme.fontWeights.medium,
//     }),
//     switchProp('disabled', {
//         true: ({ theme }) => ({
//             color: theme.colors.inStock,
//             borderRadius: 'none',
//             backgroundColor: 'transparent',
//             textDecoration: 'none',
//             '&:hover': {
//                 backgroundColor: 'transparent',
//             },
//         }),
//     })
// );

const PickupButton = styled(Button)({
    padding: '6px 16px',
    height: 'initial',
});

const StockText = styled(Text)<{ type?: StockType }>(({ type, theme }) => ({
    color: theme.colors.textSubtle,
    ...(type === 'inStock' && {
        color: theme.colors.inStock,
    }),
    ...(type === 'lowStock' && {
        color: theme.colors.lowStock,
    }),
}));
